import type { ReactNode } from 'react';
import { formatDatePattern, toDate } from '../../components/grid/format';
import type { AppGridColumn } from '../../components/grid/types';

/**
 * Columns for CardGrid (see CardGrid.tsx) and the one date format every
 * dashboard card uses.
 *
 * A card column is a plain AppGrid column. Long text (a name, a diet order, a
 * message) is `sizing: 'fill'`: it takes the width the short columns leave and
 * is the first to truncate (AppGrid's own tooltip then shows the full text).
 * Every other column is sized to its header and its widest value and never
 * shrinks (CardGrid makes that the default).
 */
export type CardColumn<T> = AppGridColumn<T>;

/**
 * Narrowest a second fill column (a diet order, an allergy, a title) gets
 * before the grid scrolls sideways: about 10 characters at the Default size.
 * A fill column without `minWidth` keeps AppGrid's default (its header, or 14
 * characters).
 */
export const CARD_FILL_MIN_WIDTH = 96;

/* ------------------------------------------------------------------ */
/*  Dates                                                              */
/* ------------------------------------------------------------------ */

/** "10/21/2026 1:05 PM"; a value with no time of day (midnight) shows the date alone. */
export function cardDateText(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  return formatDatePattern(d, hasTime(d) ? 'M/d/yyyy h:mm a' : 'M/d/yyyy');
}

/** The tooltip: "Wednesday, October 21, 2026 1:05 PM". */
export function cardDateTitle(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  return formatDatePattern(d, hasTime(d) ? 'EEEE, MMMM d, yyyy h:mm a' : 'EEEE, MMMM d, yyyy');
}

function hasTime(d: Date): boolean {
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

/** A date column in the card format, sorted by the date, with the full date in a tooltip. */
export function cardDateColumn<T>(dataField: string, caption: string, extra: Partial<CardColumn<T>> = {}): CardColumn<T> {
  return {
    dataField,
    caption,
    dataType: 'datetime',
    format: cardDateText,
    cellRender: ({ value, text }): ReactNode => <span title={cardDateTitle(value) || undefined}>{text}</span>,
    ...extra,
  };
}

/** "Yes" for true, blank otherwise (Readmit, Admit). */
export const yesOrBlank = ({ value }: { value: unknown }) => (value ? 'Yes' : '');
